import axios from 'axios'
import { supabase } from './supabase'

export interface ImageSearchResult {
  imageUrl: string
  thumbnailUrl: string
  sourceUrl?: string
  title?: string
  domain?: string
  width?: number
  height?: number
  imageData?: string
}


export interface ExternalAnalyzedImage {
  codigo: string
  filename?: string
  imageData?: string
  sizeBytes?: number
  productExists?: boolean
  imageExists?: boolean
  valid: boolean
  error?: string
  origin?: 'CATALOG' | 'MANUAL'
  manualDescription?: string
  hadImage?: boolean
  deletedAt?: string
}

export interface ProductState {
  status: 'PENDING' | 'SEARCHED' | 'SELECTED' | 'APPROVED' | 'ARCHIVED' | 'DELETED' | 'NO_IMAGE' | 'DEACTIVATED' | 'SKIPPED' | 'ERROR'
  selectedImage?: ImageSearchResult
  processedImage?: string
  updatedAt: string
  searchQuery?: string
  error?: string
  origin?: 'CATALOG' | 'MANUAL'
  manualDescription?: string
  hadImage?: boolean
  deletedAt?: string
}


export interface BrandSummary {
  brand: string
  total: number
  complete: number
  missing: number
  noImage: number
}

export interface BrandDetailItem {
  codigo: string
  descricao: string
  marca?: string
  status: ProductState['status']
  imageUrl?: string
}

export interface Product {
  codigo: string
  descricao: string
  marca?: string
}

export interface Checkpoint {
  version: number
  status: 'idle' | 'in_progress' | 'completed'
  sourceHash: string
  currentIndex: number
  currentProductCode: string | null
  totalProducts: number
  approved: number
  noImage: number
  deactivated?: number
  skipped: number
  lastUpdatedAt: string
  completedAt?: string
}

export interface Statistics {
  approved: number
  archived?: number
  noImage: number
  deactivated?: number
  skipped: number
  pending: number
  searched: number
}

const API = axios.create({
  baseURL: '/api',
  timeout: 60000
})

API.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

export const apiClient = {
  // Inicialização
  init: (products: Product[]) =>
    API.post<{ checkpoint: Checkpoint; message: string }>('/init', { products }),

  // Checkpoint
  getCheckpoint: () =>
    API.get<{ checkpoint: Checkpoint | null }>('/checkpoint'),

  resumeSession: () =>
    API.post<{ checkpoint: Checkpoint }>('/checkpoint/resume'),

  completeSession: () =>
    API.post<{ message: string; checkpoint: Checkpoint }>('/session/complete'),

  resetSession: () =>
    API.post<{ checkpoint: Checkpoint; message: string }>('/session/reset'),

  // Produtos
  getCurrentProduct: () =>
    API.get<{
      product: Product
      state: ProductState
      stats: Statistics
      checkpoint: { index: number; total: number }
    }>('/products/current'),

  getPreviousProduct: () =>
    API.post<{
      product: Product
      state: ProductState
      stats: Statistics
      checkpoint: { index: number; total: number }
    }>('/products/previous'),

  getProduct: (codigo: string) =>
    API.get<{ product: Product; state: ProductState }>(`/products/${codigo}`),

  getNextProduct: () =>
    API.post<{
      product: Product
      state: ProductState
      stats: Statistics
      checkpoint: { index: number; total: number }
    }>('/products/next'),

  jumpToProduct: (codigo: string) =>
    API.post<{ product: Product; state: ProductState; stats: Statistics; checkpoint: { index: number; total: number } }>(`/products/jump/${encodeURIComponent(codigo)}`),

  getUnclassifiedProduct: (excludeCode?: string) =>
    API.get<{ product: Product; state: ProductState }>('/products/unclassified', { params: { exclude: excludeCode || '' } }),

  // Pesquisa
  search: (productCode: string, query?: string, customQuery?: string, offset?: number) =>
    API.post<{ images: ImageSearchResult[]; query: string; count: number; error?: string; offset?: number }>(
      '/search',
      { productCode, query, customQuery, offset: offset || 0 }
    ),

  // Ações
  archiveImage: (codigo: string) =>
    API.post<{ state: ProductState; message: string }>(`/products/${codigo}/archive`),

  restoreImage: (codigo: string) =>
    API.post<{ state: ProductState; message: string }>(`/products/${codigo}/restore`),

  archiveAllImages: () =>
    API.post<{ changed: number; message: string }>('/gallery/archive-all'),

  restoreAllImages: () =>
    API.post<{ changed: number; message: string }>('/gallery/restore-all'),

  deleteProductImage: (codigo: string) =>
    API.delete<{ state: ProductState; message: string }>(`/products/${codigo}/image`),

  deleteArchivedImage: (codigo: string) =>
    API.delete<{ state: ProductState; message: string }>(`/products/${codigo}/archived-image`),

  approveImage: (codigo: string, image: ImageSearchResult, query: string, origin: 'CATALOG'|'MANUAL' = 'CATALOG', manualDescription?: string) =>
    API.post<{ state: ProductState; message: string }>(
      `/products/${codigo}/approve`,
      { image, query, origin, manualDescription }
    ),

  approveLocalFile: (codigo: string, file: File, query: string, origin: 'CATALOG'|'MANUAL' = 'CATALOG', manualDescription?: string) =>
    API.post<{ state: ProductState; message: string }>(
      `/products/${codigo}/approve-upload`,
      file,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        params: { query, origin, manualDescription: manualDescription || '' },
        timeout: 60000
      }
    ),

  markNoImage: (codigo: string) =>
    API.post<{ state: ProductState; message: string }>(`/products/${codigo}/no-image`),

  deactivateProduct: (codigo: string) =>
    API.post<{ state: ProductState; message: string }>(`/products/${codigo}/deactivate`),

  skipProduct: (codigo: string) =>
    API.post<{ state: ProductState; message: string }>(`/products/${codigo}/skip`),

  // Preview e Download Individual
  getProductImage: (codigo: string) =>
    `${API.defaults.baseURL}/products/${codigo}/image`,

  getProductImageBlob: (codigo: string) =>
    API.get(`/products/${codigo}/image`, { responseType: 'blob' }),

  downloadProductImage: (codigo: string) =>
    `${API.defaults.baseURL}/products/${codigo}/download`,

  downloadProductImageBlob: (codigo: string) =>
    API.get(`/products/${codigo}/download`, { responseType: 'blob' }),

  // Estatísticas
  getStats: () =>
    API.get<Statistics>('/stats'),

  // Limpeza de dados
  clearAllData: () =>
    API.post<{ message: string; status: string }>('/data/clear'),

  // Histórico
  getHistory: () =>
    API.get<{ sessions: Array<{ filename: string; date: string; approved: number; noImage: number; skipped: number; total: number }> }>('/history/list'),

  clearHistory: () =>
    API.post<{ message: string }>('/history/clear'),

  // Galeria
  getGallery: (params?: { status?: 'APPROVED' | 'ARCHIVED'; page?: number; pageSize?: number; search?: string }) =>
    API.get<{ items: Array<{ codigo: string; descricao: string; marca?: string; imageUrl: string; searchQuery?: string; status: 'APPROVED' | 'ARCHIVED'; origin?: 'CATALOG'|'MANUAL' }>; total: number; page: number; pageSize: number; hasMore: boolean; approvedCount: number; archivedCount: number }>('/gallery', { params }),

  exportStatusExcel: (filters: string[] = []) =>
    API.get('/export/status.xlsx', { params: { filters: filters.join(',') }, responseType: 'blob' }),

  downloadImages: (filters: string[] = []) =>
    API.get('/download/images', { params: { filters: filters.join(',') }, responseType: 'blob', timeout: 0 }),

  downloadSelectedImages: (codes: string[]) =>
    API.post('/download/selected', { codes }, { responseType: 'blob', timeout: 0 }),

  getBrandSummary: () =>
    API.get<{ brands: BrandSummary[]; totalProducts: number }>('/brands/summary'),

  getBrandDetail: (brand: string) =>
    API.get<{ brand: string; items: BrandDetailItem[] }>('/brands/detail', { params: { brand } }),

  updateBrandItemsStatus: (codes: string[], status: ProductState['status']) =>
    API.post<{ changed: number; message: string }>('/brands/bulk-status', { codes, status }),

  manualSearch: (codigo: string, descricao: string, offset = 0) =>
    API.post<{images: ImageSearchResult[]; query: string; count: number; offset: number}>('/manual/search', { codigo, descricao, offset }),

  analyzeExternalImages: (items: Array<{ codigo: string; imageData: string; filename?: string }>) =>
    API.post<{ items: ExternalAnalyzedImage[] }>('/external-images/analyze', { items }),

  importExternalImages: (items: Array<{ codigo: string; imageData: string; filename?: string }>) =>
    API.post<{ imported: number; message: string }>('/external-images/import', { items }),

  downloadExternalZip: (items: Array<{ codigo: string; imageData: string; filename?: string }>) =>
    API.post('/external-images/zip', { items }, { responseType: 'blob' }),

  // Cache de upload
  uploadCache: (filename: string, content: string) =>
    API.post<{ message: string; filename: string; cacheFile: string }>('/upload/cache', { filename, content }),

  getCache: (filename: string) =>
    API.get<{ filename: string; content: string; found: boolean }>(`/upload/cache/${filename}`),

  deleteCache: (filename: string) =>
    API.delete<{ message: string }>(`/upload/cache/${filename}`),

  clearAllCache: () =>
    API.post<{ message: string }>('/upload/cache/clear-all'),

  // Health
  health: () =>
    API.get<{ status: string; timestamp: string }>('/health')
}

export function getApiError(error: unknown, fallback = 'Erro inesperado'): string {
  if (axios.isAxiosError(error)) return (error.response?.data as any)?.error || error.message || fallback
  return error instanceof Error ? error.message : fallback
}
